<template>
  <div v-if="loaded" class="table-container">
    <table class="table hide-medium" border="0" cellspacing="0" cellpadding="0">
      <thead class="table-header">
        <tr>
          <th v-for="data in headers" :key="data" class="table-header-item">{{ data }}</th>
        </tr>
      </thead>

      <tbody v-if="!loading" class="table-content">
        <tr v-for="company in tableValues" :key="company.siren" class="table-line">
          <td class="table-item read name">
            <div v-if="company.companyName" class="item" @click="$router.push(`search/${company.siren}`)">
              {{ company.companyName }}
            </div>
            <div v-else class="item">-</div>
          </td>

          <td class="table-item read monitoring">
            <div class="item container-item">
              <MonitoringType
                v-if="getMonitoringInfos(company.packageLevelPublicId)"
                :level="getMonitoringInfos(company.packageLevelPublicId).level"
                :label="getMonitoringInfos(company.packageLevelPublicId).label"
              />
              <div v-else class="item none">-</div>
            </div>
          </td>

          <td class="table-item read">
            <div v-if="company.internalRef" class="item">{{ company.internalRef }}</div>
            <div v-else class="item">-</div>
          </td>

          <td class="table-item read siren">
            <div v-if="company.siren" class="item">{{ company.siren }}</div>
            <div v-else class="item">-</div>
          </td>

          <td class="table-item read action">
            <div class="item">
              <ActionType :config="{ style: '', showWording: true }" :company="company" />
            </div>
          </td>

          <td class="table-item read">
            <div v-if="company.managerPublicId" class="item">
              {{ getManagerInfos(company.managerPublicId).firstName }}
              {{ getManagerInfos(company.managerPublicId).lastName }}
            </div>
            <div v-else class="item">-</div>
          </td>
        </tr>
      </tbody>
    </table>
    <Pagination
      v-if="tableValues && tableValues.length && config.pagination"
      :pagination="pagination"
      :set-page="setPage"
      :tableValues="tableValues"
      :pages="pages"
      :disable-dots="disableDots"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ActionType from '@/components/actionType';
import MonitoringType from '@/components/monitoringType';
import Pagination from '@/components/Pagination';
import tableMixin from '@/components/tables/TableMixin';

export default {
  components: { Pagination, ActionType, MonitoringType },
  mixins: [tableMixin],

  props: {
    config: {
      type: Object,
      required: false,
      default: () => ({ pagination: false }),
    },

    filters: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      headers: [this.$t('companyLabel'), this.$t('monitoring'), this.$t('id'), this.$t('SIREN'), this.$t('action'), this.$t('manager')],
      values: [],
      tableMixinConfig: {
        backendPaginated: true,
        onChange: this.changePage,
        getData: this.getData,
        maxElement: 5,
      },
      loaded: false,
      loading: false,
    };
  },

  computed: {
    ...mapGetters(['user']),
  },

  watch: {
    async 'filters.action'(newValue, oldValue) {
      if (newValue !== oldValue) this.setPage(0);
    },

    async 'filters.search'(newValue, oldValue) {
      if (newValue !== oldValue) this.setPage(0);
    },

    async 'filters.paginationNumber'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.tableMixinConfig.maxElement = newValue;
        this.pagination.maxElement = newValue;
        this.setPage(0);
      }
    },
  },

  async mounted() {
    this.packages = (await this.$store.dispatch('getPackagesLevel')).items;
    this.users = (await this.$store.dispatch('getUsers', { id: this.user.client.id })).users;
    this.loaded = true;
  },

  methods: {
    async getData(pageNumber = 0, pageSize = this.tableMixinConfig.maxElement) {
      let data;
      data = await this.$store.dispatch('getHistoryImportById', {
        pageNumber,
        pageSize,
        id: this.$route.query.id,
        search: this.filters.search,
        action: this.filters.action,
      });

      this.values = data.importsHistories.items;
      this.pagination.totalLength = data.count;
      this.pagination.maxPage = Math.ceil(this.pagination.totalLength / this.pagination.maxElement);
      return {
        data: data.importsHistories,
        values: data.importsHistories.items,
      };
    },

    tableItemClass(alert) {
      let result = '';
      if (alert.read) result += 'read ';
      return result;
    },

    getMonitoringInfos(packageId) {
      const info = this.packages.find(value => value.publicId === packageId);
      return info;
    },

    getManagerInfos(managerId) {
      const info = this.users.find(value => value.id === managerId);
      return info;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.table .table-content .table-line .table-item,
.table-container .table-line .line .line-content {
  &.table-item {
    color: colors.$black-color !important;
    font-weight: 400;

    &.siren,
    &.monitoring {
      width: 170px;
    }

    &.action,
    &.name {
      width: 250px;
    }

    &.name .item {
      opacity: 0.8;
      cursor: pointer;
      transition: all 0.3s;
      padding: 0 8px;
    }
  }

  &.monitoring {
    .container-item .item {
      &:not(.none) {
        &:nth-child(2n) {
          color: colors.$red-color !important;
          font-size: 14px;
        }

        &:not(:nth-child(2n)) {
          flex-shrink: 0;
          width: 17px;
          height: 17px;
          background: colors.$red-color;
          color: colors.$white-color !important;
          border-radius: 50%;
          font-size: 12px;
          margin: 0 8px 0 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }

  &.edit {
    .item {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px 8px;
      font-size: 12px;
      background: colors.$grey-color;
      border-radius: 4px;
      color: colors.$white-color;
    }
  }

  &.read-indication {
    .item {
      position: relative;
      padding-left: 12px;

      &:nth-child(2n + 1):before {
        position: absolute;
        left: 0px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        content: '';
        display: flex;
        align-items: center;
        justify-content: center;
        background: colors.$green-color;
      }
    }

    &.read .item:nth-child(2n + 1):before {
      width: 5px;
      height: 5px;
      border: 1px solid colors.$grey-color;
      background: colors.$read-table-color;
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-medium) {
  .table-container {
    width: 100%;

    .table-line {
      width: 100%;
      height: 192px;
      margin: 12px 0;
      display: flex;
      flex-direction: column;

      background: colors.$unread-table-color;
      padding: 20px;
      box-sizing: border-box;
      border-radius: 4px;

      &.read {
        background: rgba(colors.$lightgrey-color, 0.2);

        .item {
          color: colors.$grey-color;
        }
      }

      .line {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 1;

        &.right {
          justify-content: flex-end;
        }

        .line-content {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .item {
            display: flex;
            align-items: center;
          }

          &.read-indication {
            align-self: flex-end;
          }

          &.label,
          &.date {
            font-weight: 500;
            color: colors.$black-color;
          }

          &.date,
          .internal-id {
            font-size: 12px;
          }

          &.label {
            font-size: 16px;
          }
        }
      }
    }
  }
}
</style>
